import React, { useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRotateRight } from "@fortawesome/free-solid-svg-icons";
import CustomTooltip from "../../../../../../Elements/Tooltip";

const normalizeAngle = (deg) => ((Math.round(deg) % 360) + 360) % 360;

const FlipHorizontalIcon = () => (
  <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
    <path d="M8 4 L2 10 L8 16 Z" fill="currentColor" />
    <path d="M12 4 L18 10 L12 16 Z" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    <line x1="10" y1="2" x2="10" y2="18" stroke="currentColor" strokeWidth="1.5" strokeDasharray="2 2" />
  </svg>
);

const FlipVerticalIcon = () => (
  <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
    <path d="M4 8 L10 2 L16 8 Z" fill="currentColor" />
    <path d="M4 12 L10 18 L16 12 Z" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    <line x1="2" y1="10" x2="18" y2="10" stroke="currentColor" strokeWidth="1.5" strokeDasharray="2 2" />
  </svg>
);

const RotateSection = ({ tool, setTool, handleSubmitBtnState }) => {
  const dialRef = useRef(null);
  const angle = Number(tool?.rotate) || 0;

  const setAngle = (value) => {
    setTool((prev) => ({
      ...prev,
      rotate: value === '' ? '' : normalizeAngle(Number(value)),
    }));
    handleSubmitBtnState();
  };

  const flipHorizontal = () => {
    setTool((prev) => ({ ...prev, flip: prev?.flip === -1 ? 1 : -1 }));
    handleSubmitBtnState();
  };

  const flipVertical = () => {
    setTool((prev) => ({
      ...prev,
      flip: prev?.flip === -1 ? 1 : -1,
      rotate: normalizeAngle((Number(prev?.rotate) || 0) + 180),
    }));
    handleSubmitBtnState();
  };

  useEffect(() => {
    if (tool.rotate > 360) {
      setAngle(0);
    }
  }, [tool.rotate]);

  const angleFromPointer = (event) => {
    const rect = dialRef.current.getBoundingClientRect();
    const dx = event.clientX - (rect.left + rect.width / 2);
    const dy = event.clientY - (rect.top + rect.height / 2);
    let deg = (Math.atan2(dy, dx) * 180) / Math.PI + 90;
    if (event.shiftKey) deg = Math.round(deg / 15) * 15;
    return normalizeAngle(deg);
  };

  const handlePointerDown = (event) => {
    event.preventDefault();
    dialRef.current.setPointerCapture?.(event.pointerId);
    setAngle(angleFromPointer(event));
  };

  const handlePointerMove = (event) => {
    if (!dialRef.current.hasPointerCapture?.(event.pointerId)) return;
    setAngle(angleFromPointer(event));
  };

  const handleDialKey = (event) => {
    const step = event.shiftKey ? 15 : 1;
    if (event.key === 'ArrowRight' || event.key === 'ArrowUp') {
      event.preventDefault();
      setAngle(angle + step);
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') {
      event.preventDefault();
      setAngle(angle - step);
    }
  };

  return (
    <div className="pv-transform">
      <div className="pv-transform-group">
        <span className="pv-transform-label">Rotate</span>
        <div className="pv-rotate">
          <CustomTooltip title={<p className="p-1 text-light mb-0">Drag to rotate. Hold Shift to snap to 15°.</p>}>
            <div
              ref={dialRef}
              className="pv-dial"
              role="slider"
              tabIndex={0}
              aria-label="Rotation"
              aria-valuemin={0}
              aria-valuemax={359}
              aria-valuenow={angle}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onKeyDown={handleDialKey}
            >
              <span className="pv-dial-hand" style={{ transform: `rotate(${angle}deg)` }} />
              <span className="pv-dial-center" />
            </div>
          </CustomTooltip>
          <label className="pv-angle">
            <FontAwesomeIcon icon={faRotateRight} />
            <input
              type="number"
              name="rotate"
              min={0}
              max={359}
              value={tool?.rotate ?? ''}
              onChange={(e) => setAngle(e.target.value)}
              onFocus={(e) => e.target.select()}
              onKeyDown={(evt) => ["e", "E", "+"].includes(evt.key) && evt.preventDefault()}
            />
            <span>°</span>
          </label>
        </div>
      </div>
      <div className="pv-transform-group">
        <span className="pv-transform-label">Flip</span>
        <div className="pv-flip-buttons">
          <CustomTooltip title={<p className="p-1 text-light mb-0">Flip horizontally (mirror left to right)</p>}>
            <button type="button" className="pv-icon-btn" aria-label="Flip horizontally" onClick={flipHorizontal}>
              <FlipHorizontalIcon />
            </button>
          </CustomTooltip>
          <CustomTooltip title={<p className="p-1 text-light mb-0">Flip vertically (upside down)</p>}>
            <button type="button" className="pv-icon-btn" aria-label="Flip vertically" onClick={flipVertical}>
              <FlipVerticalIcon />
            </button>
          </CustomTooltip>
        </div>
      </div>
    </div>
  );
};
export default RotateSection;
